// pwa.ts — registrar el trabajador de servicio, avisar de una versión nueva, y
// ofrecer instalar.
//
// ═══════════════════════════════════════════════════════════════════════════
// ▶ POR QUÉ ESTO EXISTE EN BOOKS, Y NO EXISTÍA
// ═══════════════════════════════════════════════════════════════════════════
//
// Los otros tres productos de la casa lo tienen desde hace tiempo: el hub con
// su `instalar.ts`, Backlog con su `pwa.ts`. Books no tenía **nada** — ni
// manifiesto, ni trabajador, ni botón — así que ni se podía instalar ni se
// enteraba nadie de que había una versión nueva. Se medió antes de escribir
// esto: cero coincidencias de `serviceWorker`, `manifest` y
// `beforeinstallprompt` en todo el repositorio.
//
// Y el modo de fallo de que faltara era el de siempre acá: **no se veía.** Una
// pestaña abierta desde ayer sigue sirviendo el paquete de ayer y no dice nada.
// En un sistema contable eso significa que dos personas pueden estar mirando
// dos versiones del mismo balance sin saberlo.
//
// ═══════════════════════════════════════════════════════════════════════════
// ▶▶ AVISAR, NO RECARGAR
// ═══════════════════════════════════════════════════════════════════════════
//
// Cuando hay una versión nueva esperando, esto NO recarga la pestaña: llama al
// aviso y la persona decide. Alguien tecleando un asiento de doce líneas
// perdería lo que lleva escrito, y ni siquiera sabría por qué se le fue.
//
// Es el mismo criterio que el resto del producto: lo que tiene consecuencia se
// pregunta. Cerrar un mes se confirma, abrir un ejercicio se confirma, y tirar
// el trabajo de alguien también.
//
// ═══════════════════════════════════════════════════════════════════════════
// ▶ EN DESARROLLO NO SE REGISTRA
// ═══════════════════════════════════════════════════════════════════════════
//
// Un trabajador de servicio cacheando el cascarón durante el desarrollo es la
// forma más rápida de pasar media hora preguntándose por qué un cambio no se
// ve. Y peor acá: la caminata sirve un paquete compilado en `dist-caminata/`,
// así que un trabajador vivo podría servir el cascarón de una caminata anterior
// sobre la base desechable de hoy.

/** Qué hacer cuando hay una versión esperando. */
export type AvisoDeVersion = (relevar: () => void) => void;

export function registrarTrabajador(avisar: AvisoDeVersion): void {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) return;

  globalThis.addEventListener('load', () => {
    // ▶▶ `updateViaCache: 'none'`, y NO una cabecera en `_headers`.
    //
    // Se intentó con `Cache-Control: no-cache` sobre `/sw.js` y **no se
    // aplicó**: Cloudflare Pages sirve su propio `max-age=14400` para lo
    // estático y manda él. Se midió contra producción — el resto del bloque
    // `/*` sí llega (CSP, X-Frame, Permissions), o sea que `_headers` funciona
    // y esa cabecera concreta es suya.
    //
    // Lo que quedaba era una declaración en un archivo que no hacía nada, que
    // es peor que no tenerla: alguien la lee y cree que está resuelto. Acá sí
    // mandamos nosotros, y dice lo mismo con una garantía de verdad.
    //
    // (El valor por omisión es `'imports'`, que ya salta el caché para el
    // guion principal. Ponerlo explícito es que no dependa de un default.)
    void navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((registro) => {
      // Ya había una esperando cuando se abrió la pestaña.
      if (registro.waiting) avisar(() => relevar(registro));

      registro.addEventListener('updatefound', () => {
        const nuevo = registro.installing;
        if (!nuevo) return;
        nuevo.addEventListener('statechange', () => {
          // `installed` CON un controlador vivo significa que hay una versión
          // nueva lista y una vieja atendiendo. Sin controlador es la primera
          // instalación, y ahí no hay nada que avisar — avisarlo sería ofrecer
          // actualizar a quien acaba de abrir la aplicación por primera vez.
          if (nuevo.state === 'installed' && navigator.serviceWorker.controller) {
            avisar(() => relevar(registro));
          }
        });
      });
    }).catch(() => {
      // Que no se pueda registrar no rompe nada: la aplicación funciona igual,
      // sólo que sin cascarón guardado y sin aviso de versión. No se le dice
      // nada a nadie — un error acá no es un problema de quien lleva los libros.
    });
  });
}

function relevar(registro: ServiceWorkerRegistration): void {
  registro.waiting?.postMessage('relevar');
  // Una sola recarga, cuando el nuevo tome el control. Sin la bandera, el
  // evento puede llegar dos veces y la página se recarga en bucle.
  let recargado = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (recargado) return;
    recargado = true;
    globalThis.location.reload();
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// ▶ INSTALAR — y por qué hay un botón y no sólo un manifiesto
// ═══════════════════════════════════════════════════════════════════════════
//
// Con el manifiesto y el trabajador, el navegador **puede** ofrecer instalar.
// Dónde lo ofrece es cosa suya: en Chrome de escritorio es un ícono dentro de
// la barra de direcciones que hay que conocer; en Android sale a veces como una
// tira que se cierra sola.
//
// O sea que sin botón, «se puede instalar» es cierto y **nadie lo sabe** — que
// es la forma que esta casa lleva anotada: *una posibilidad que no aterriza en
// ningún sitio visible no es una posibilidad, es la descripción de una.*
//
// ▶ Y NO SE PIDE SOLO. El evento se guarda y el botón aparece; nadie ve un
// diálogo que no pidió. Un aviso de instalación al abrir es la primera cosa que
// alguien aprende a cerrar sin leer, y después cierra igual el que importa.
//
// ▶ En iOS no hay evento: Safari no lo implementa, así que el botón no aparece
// ahí nunca. Eso no es un defecto de esta función y conviene no perseguirlo.

/** Lo que el navegador guarda para ofrecer la instalación. No está en lib.dom. */
interface EventoDeInstalacion extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let guardado: EventoDeInstalacion | null = null;
const suscritos = new Set<(puede: boolean) => void>();

function avisarInstalacion() {
  for (const f of suscritos) f(guardado !== null);
}

if (typeof globalThis.addEventListener === 'function') {
  globalThis.addEventListener('beforeinstallprompt', (e) => {
    // Se frena para que el navegador no lo enseñe por su cuenta y lo enseñemos
    // nosotros donde tiene sentido.
    e.preventDefault();
    guardado = e as EventoDeInstalacion;
    avisarInstalacion();
  });

  globalThis.addEventListener('appinstalled', () => {
    guardado = null;
    avisarInstalacion();
  });
}

/** ¿Está corriendo ya como aplicación instalada? */
export function yaInstalada(): boolean {
  return globalThis.matchMedia?.('(display-mode: standalone)').matches === true
    // Safari en iOS no soporta `display-mode`, y ésta es su forma.
    || (globalThis.navigator as { standalone?: boolean }).standalone === true;
}

export function sePuedeInstalar(): boolean {
  return guardado !== null;
}

export function alCambiarInstalacion(f: (puede: boolean) => void): () => void {
  suscritos.add(f);
  return () => suscritos.delete(f);
}

/** Devuelve `true` si la instaló. Un `false` no es un error: dijo que no. */
export async function instalar(): Promise<boolean> {
  if (!guardado) return false;
  await guardado.prompt();
  const { outcome } = await guardado.userChoice;
  // El evento no se puede volver a usar, lo haya aceptado o no.
  guardado = null;
  avisarInstalacion();
  return outcome === 'accepted';
}
